import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";

const NOT_DONE = "Payment not completed — you have not been charged";

const Shell = ({ icon, title, children, testid }) => (
  <div className="fade-up mx-auto max-w-lg rounded-lg border bg-card p-8 text-center" data-testid={testid}>
    <div className="flex justify-center">{icon}</div>
    <h1 className="mt-4 text-2xl font-semibold" data-testid="payment-result-title">{title}</h1>
    <div className="mt-4 space-y-4">{children}</div>
  </div>
);

export default function PaymentResult() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [state, setState] = useState({ phase: "checking", data: null });

  useEffect(() => {
    let alive = true;
    if (cancelled) {
      api.post("/portal/payments/cancel", { tx_id: cancelled })
        .then((r) => alive && setState({ phase: r.data.payment_status === "paid" ? "paid" : "failed", data: r.data }))
        .catch(() => alive && setState({ phase: "failed", data: null }));
      return () => { alive = false; };
    }
    let n = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/portal/payments/${sid}`);
        if (!alive) return;
        if (data.payment_status === "paid") return setState({ phase: "paid", data });
        if (data.status === "processing") return setState({ phase: "processing", data });
        if (["cancelled", "expired", "failed"].includes(data.status)) return setState({ phase: "failed", data });
        if (++n >= 10) return setState({ phase: "timeout", data });
        setTimeout(poll, 2000);
      } catch {
        if (alive) setState({ phase: "failed", data: null });
      }
    };
    poll();
    return () => { alive = false; };
  }, [sid, cancelled]);

  const d = state.data;
  const loanLink = d ? `/portal/loans/${d.loan_id}` : "/portal";
  if (state.phase === "checking") {
    return <Shell testid="payment-checking" icon={<Loader2 className="h-12 w-12 animate-spin text-primary" />} title="Confirming your payment…"><p className="text-sm text-muted-foreground">This only takes a moment.</p></Shell>;
  }
  if (state.phase === "paid") {
    return (
      <Shell testid="payment-success" icon={<CheckCircle2 className="h-14 w-14 text-emerald-600" />} title="Payment received — thank you">
        <div className="num font-display text-4xl font-semibold" data-testid="payment-success-amount">{money(d.amount_cents)}</div>
        <div className="mx-auto max-w-xs divide-y rounded-md border text-sm">
          <div className="flex justify-between px-4 py-2.5"><span className="text-muted-foreground">Loan</span><span className="mono">{d.loan_number}</span></div>
          <div className="flex justify-between px-4 py-2.5"><span className="text-muted-foreground">New balance (payoff)</span><span className="num font-semibold" data-testid="payment-success-new-balance">{money(d.payoff_cents)}</span></div>
          <div className="flex justify-between px-4 py-2.5"><span className="text-muted-foreground">Principal balance</span><span className="num">{money(d.principal_balance_cents)}</span></div>
        </div>
        <p className="text-xs text-muted-foreground">A receipt has been emailed to you.</p>
        <Button asChild data-testid="payment-back-to-loan"><Link to={loanLink}>View loan</Link></Button>
      </Shell>
    );
  }
  if (state.phase === "processing") {
    return (
      <Shell testid="payment-processing" icon={<Clock className="h-14 w-14 text-[hsl(var(--gold))]" />} title="Bank payment submitted">
        <div className="num font-display text-4xl font-semibold" data-testid="payment-processing-amount">{money(d.amount_cents)}</div>
        <p className="text-sm font-semibold" data-testid="payment-processing-message">Pending — usually clears in 3–5 business days</p>
        <p className="text-xs text-muted-foreground">We'll apply it to {d.loan_number} once your bank confirms it and email you a receipt. Your balance won't change until then.</p>
        <Button asChild data-testid="payment-back-to-loan"><Link to={loanLink}>View loan</Link></Button>
      </Shell>
    );
  }
  if (state.phase === "timeout") {
    return (
      <Shell testid="payment-pending" icon={<Loader2 className="h-12 w-12 text-muted-foreground" />} title="Still confirming your payment">
        <p className="text-sm text-muted-foreground">We haven't received confirmation from the card processor yet. If you were charged, it will appear on your loan shortly — it will never be recorded twice.</p>
        <Button asChild variant="outline"><Link to={loanLink}>Back to loan</Link></Button>
      </Shell>
    );
  }
  return (
    <Shell testid="payment-not-completed" icon={<XCircle className="h-14 w-14 text-destructive" />} title={d?.status === "failed" ? "Your bank payment didn't go through" : NOT_DONE}>
      {d?.status === "failed" && <p className="text-sm text-muted-foreground" data-testid="payment-failed-reason">{d.message}. Nothing was applied to your loan.</p>}
      <div className="flex flex-wrap justify-center gap-2">
        {d && <Button asChild data-testid="payment-try-again"><Link to={`/portal/loans/${d.loan_id}/pay`}>Try again</Link></Button>}
        <Button asChild variant="outline" data-testid="payment-back-to-loans"><Link to="/portal">My loans</Link></Button>
      </div>
    </Shell>
  );
}
